import { useEffect, useState } from 'react';
import { Control, FieldErrors, useController } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import PaperSection from '@rossum/components/paper-section/PaperSection';
import { IconSettings } from '@rossum/ui/icons/tabler';
import {
  Alert,
  AlertTitle,
  Divider,
  FormControlLabel,
  Skeleton,
  Stack,
  SvgIcon,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import SelectItems from '../../../../../components/ReactHookForm/SelectItems';
import { documentRemovalSelector } from '../../../../../features/pricing/selectors';
import { boldText, link } from '../../../../../lib/formaterValues';
import DefaultAlignedToggle from '../../../components/DefaultAlignedToggle';
import styles from '../../../styles.module.sass';
import { QueueSettingsData } from './formType';
import QueueSettingsLabel from './QueueSettingsLabel';

type Props = {
  suggestedEditEnabled: boolean;
  control: Control<QueueSettingsData>;
  isTriageReady: boolean;
  openTriageSettings: () => void;
  errors: FieldErrors<QueueSettingsData>;
  initialDocumentLifetimeValue: string | null;
};

const Placeholder = () => (
  <Typography variant="body2" sx={{ visibility: 'hidden' }}>
    - <br /> -
  </Typography>
);

const Document = ({
  suggestedEditEnabled,
  control,
  isTriageReady,
  openTriageSettings,
  errors,
  initialDocumentLifetimeValue,
}: Props) => {
  const intl = useIntl();

  const {
    field: { value: triageValue },
  } = useController({
    name: 'triageStateEnabled',
    control,
  });

  const { field: documentLifetimeField } = useController({
    name: 'documentLifetime',
    control,
  });

  const [isDocumentRemovalOn, setIsDocumentRemovalOn] = useState(
    !!initialDocumentLifetimeValue
  );

  const isDocumentRemovalEnabled = useSelector(documentRemovalSelector);

  useEffect(() => {
    setIsDocumentRemovalOn(!!initialDocumentLifetimeValue);
  }, [initialDocumentLifetimeValue]);

  const shouldAlertForDocumentRemoval = !initialDocumentLifetimeValue;

  return (
    <PaperSection
      title={intl.formatMessage({
        id: 'containers.settings.queues.titles.document',
      })}
    >
      <Stack spacing={4} useFlexGap divider={<Divider />}>
        <Stack>
          <div className={styles['Row']}>
            <div>
              <QueueSettingsLabel
                titleId="containers.settings.queues.splitBatchFiles.label"
                tooltipId="containers.settings.queues.splitBatchFiles.tooltip"
              />
            </div>
            <div data-cy="basic-setting-split-batch-select">
              <Tooltip
                title={
                  suggestedEditEnabled
                    ? ''
                    : intl.formatMessage(
                        {
                          id: 'containers.settings.queues.splitBatchFiles.unavailable',
                        },
                        { link: link('mailto:support@rossum.ai') }
                      )
                }
              >
                <div>
                  <SelectItems<
                    QueueSettingsData,
                    NonNullable<QueueSettingsData['suggestedEdit']>
                  >
                    selectDisabled={!suggestedEditEnabled}
                    control={control}
                    name="suggestedEdit"
                    placeholderKey="containers.settings.queues.splitBatchFiles.placeholder"
                    options={[{ value: 'disable' }, { value: 'suggest' }]}
                    getLabel={value =>
                      intl.formatMessage({
                        id: `containers.settings.queues.splitBatchFiles.${value}.title`,
                      })
                    }
                    withDescription
                    getDescription={value =>
                      intl.formatMessage({
                        id: `containers.settings.queues.splitBatchFiles.${value}.description`,
                      })
                    }
                  />
                </div>
              </Tooltip>
            </div>
          </div>
          {/* Hide if triage value did not load successfully */}
          {isTriageReady && triageValue === undefined ? null : (
            <Stack maxWidth={600} spacing={2}>
              {isTriageReady ? (
                <DefaultAlignedToggle
                  name="triageStateEnabled"
                  control={control}
                  onClick={openTriageSettings}
                  buttonIcon={
                    <SvgIcon fontSize="large">
                      <IconSettings />
                    </SvgIcon>
                  }
                  buttonTitle={intl.formatMessage({
                    id: 'containers.settings.queues.triageState.button',
                  })}
                  dataCy="queue-settings-triage-state"
                >
                  {intl.formatMessage({
                    id: 'containers.settings.queues.triageState.sublabel',
                  })}
                </DefaultAlignedToggle>
              ) : (
                <Skeleton height={36} width="100%" />
              )}
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({
                  id: 'containers.settings.queues.triageState.description',
                })}
              </Typography>
            </Stack>
          )}
        </Stack>
        {isDocumentRemovalEnabled && (
          <Stack spacing={1} sx={{ maxWidth: 600 }} useFlexGap>
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Stack
                direction="row"
                sx={{ alignItems: 'center', alignSelf: 'flex-start' }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isDocumentRemovalOn}
                      onChange={(_, value) => {
                        if (!value) {
                          // Reset document lifetime when document removal is disabled
                          documentLifetimeField.onChange('');
                        }
                        setIsDocumentRemovalOn(value);
                      }}
                    />
                  }
                  label={intl.formatMessage({
                    id: 'containers.settings.queues.documentLifetime.label',
                  })}
                />
              </Stack>
              <TextField
                {...documentLifetimeField}
                label={intl.formatMessage({
                  id: 'containers.settings.queues.documentLifetime.fieldLabel',
                })}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Typography color="text.secondary">
                        {intl.formatMessage({
                          id: 'containers.settings.queues.documentLifetime.fieldUnit',
                        })}
                      </Typography>
                    ),
                  },
                  formHelperText: { component: 'div' },
                }}
                helperText={
                  <Stack>
                    {errors.documentLifetime ? (
                      <Typography color="error" variant="body2">
                        {errors.documentLifetime.message}
                      </Typography>
                    ) : documentLifetimeField.value ? (
                      <Typography color="text.secondary" variant="body2">
                        {intl.formatMessage(
                          {
                            id: 'containers.settings.queues.documentLifetime.helperText',
                          },
                          {
                            retentionPeriod: documentLifetimeField.value,
                            boldText,
                          }
                        )}
                      </Typography>
                    ) : (
                      <Placeholder />
                    )}
                  </Stack>
                }
                size="small"
                sx={{ width: 336 }}
                disabled={!isDocumentRemovalOn}
              />
            </Stack>

            {shouldAlertForDocumentRemoval && isDocumentRemovalOn && (
              <Alert severity="warning">
                <AlertTitle>
                  {intl.formatMessage({
                    id: 'containers.settings.queues.documentLifetime.alert.title',
                  })}
                </AlertTitle>
                <Typography>
                  {intl.formatMessage({
                    id: 'containers.settings.queues.documentLifetime.alert.description',
                  })}
                </Typography>
              </Alert>
            )}
          </Stack>
        )}
      </Stack>
    </PaperSection>
  );
};

export default Document;
